import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { HelmetProvider } from 'react-helmet-async';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { db } from './config/firebase';
import Header from './components/layout/Header';
import Login from './pages/Login';
import Signup from './pages/Signup';
import Dashboard from './pages/Dashboard';
import ImportCSV from './pages/ImportCSV';
import AuctionList from './pages/AuctionList';
import AuctionDetails from './pages/AuctionDetails';
import UpdateImages from './pages/UpdateImages';
import UpdateAuctionCSV from './pages/UpdateAuctionCSV';
import LotDetail from './pages/LotDetail';
import BidderRegistration from './pages/BidderRegistration';
import PublicAuctionList from './pages/PublicAuctionList';
import PastAuctions from './pages/PastAuctions';
import PastSaleResults from './pages/PastSaleResults';
import PastLotDetail from './pages/PastLotDetail';
import Home from './pages/Home';
import Profile from './pages/Profile';
import MyBids from './pages/MyBids';
import MyInvoices from './pages/MyInvoices';
import MyInvoiceDetail from './pages/MyInvoiceDetail';
import InvoiceList from './pages/InvoiceList';
import InvoiceDetail from './pages/InvoiceDetail';
import PickerInterface from './pages/PickerInterface';
import ShippingStation from './pages/ShippingStation';
import PickOrderView from './pages/PickOrderView';
import PickupConfirmation from './pages/PickupConfirmation';
import PickupOrders from './pages/PickupOrders';
import SalePage from './pages/SalePage';
import SaleInfo from './pages/SaleInfo';
import SaleConsole from './pages/SaleConsole';
import SaleDashboard from './pages/admin/SaleDashboard';
import SaleRegistrations from './pages/admin/SaleRegistrations';
import LotStats from './pages/admin/LotStats';
import AdminBidders from './pages/admin/AdminBidders';
import AdminBids from './pages/admin/AdminBids';
// ImportOrders disabled until orderImport util is wired up — wasn't blocking Sunday test.
// import ImportOrders from './pages/ImportOrders';
import SalePicker from './pages/SalePicker';
import VerifyPage from './pages/Verify/VerifyPage';
import Watching from './pages/Watching';
import ConsignorDashboard from './pages/ConsignorDashboard';
import ConsignorContractView from './pages/ConsignorContractView';
import ConsignorCOView from './pages/ConsignorCOView';
import ConsignorSettlementView from './pages/ConsignorSettlementView';
import AdminConsignors, {
  ConsignorPreviewDashboard,
  ConsignorPreviewContract,
  ConsignorPreviewCO,
  ConsignorPreviewSettlement,
} from './pages/admin/AdminConsignors';
import AdminSaleSettlements from './pages/admin/AdminSaleSettlements';


import './App.css';

const PrivateRoute: React.FC<{ children: React.ReactElement }> = ({ children }) => {
  const { currentUser } = useAuth();
  return currentUser ? children : <Navigate to="/login" />;
};

const SellerRoute: React.FC<{ children: React.ReactElement }> = ({ children }) => {
  const { currentUser, userProfile, loading } = useAuth();
  if (loading) return <div className="text-center py-12 text-gray-500">Loading…</div>;
  if (!currentUser) return <Navigate to="/login" replace />;
  if (!userProfile) return <div className="text-center py-12 text-gray-500">Loading…</div>;
  const isSeller = ['seller', 'admin', 'consignor', 'auctioneer'].includes(userProfile.role);
  return isSeller ? children : <Navigate to="/" replace />;
};

/**
 * STRICT admin guard — deliberately EXCLUDES 'consignor'.
 * SellerRoute admits consignors, but these views expose BIDDER PII (names, emails, bidder
 * numbers, who holds the high bid). A consignor must never see the demand side, so admin
 * sale views hang off this guard, not SellerRoute.
 */
const AdminRoute: React.FC<{ children: React.ReactElement }> = ({ children }) => {
  const { currentUser, userProfile, loading } = useAuth();
  if (loading) return <div className="text-center py-12 text-gray-500">Loading…</div>;
  if (!currentUser) return <Navigate to="/login" replace />;
  if (!userProfile) return <div className="text-center py-12 text-gray-500">Loading…</div>;
  const isAdmin = ['seller', 'admin', 'auctioneer'].includes(userProfile.role);
  return isAdmin ? children : <Navigate to="/" replace />;
};

/**
 * Consignor guard — access is granted by HAVING a consignors/{uid} record (doc id =
 * auth uid), NOT by role. The real consignors kept their existing (bidder) roles, so
 * a role check would lock them out. Admits when EITHER:
 *   - a consignors/{uid} record exists (they are a consignor), OR
 *   - the user is staff (admin/auctioneer).
 * The record check is async (a direct getDoc — rules-safe since doc id == uid), so the
 * guard shows a loading state until it resolves, then admits/denies — never bounces a
 * valid consignor prematurely on the race. 'seller' is NOT staff here (the consignor
 * rules are owner-or-isPaymentStaff, so a seller's reads would be denied anyway).
 */
const ConsignorRoute: React.FC<{ children: React.ReactElement }> = ({ children }) => {
  const { currentUser, userProfile, loading } = useAuth();
  const [checking, setChecking] = useState(true);
  const [hasRecord, setHasRecord] = useState(false);

  const isStaff = !!userProfile && ['admin', 'auctioneer'].includes(userProfile.role);

  useEffect(() => {
    let active = true;
    if (!currentUser || isStaff) { setChecking(false); return; }
    setChecking(true);
    (async () => {
      try {
        const snap = await getDoc(doc(db, 'consignors', currentUser.uid));
        if (active) setHasRecord(snap.exists());
      } catch {
        if (active) setHasRecord(false);
      } finally {
        if (active) setChecking(false);
      }
    })();
    return () => { active = false; };
  }, [currentUser, isStaff]);

  if (loading) return <div className="text-center py-12 text-gray-500">Loading…</div>;
  if (!currentUser) return <Navigate to="/login" replace />;
  if (!userProfile) return <div className="text-center py-12 text-gray-500">Loading…</div>;
  if (isStaff) return children;
  if (checking) return <div className="text-center py-12 text-gray-500">Loading…</div>;
  return hasRecord ? children : <Navigate to="/" replace />;
};

function AppRoutes() {
  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <main className="container mx-auto px-4 py-8">
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/signup" element={<Signup />} />
          {/* Public TTR verification page — scanned from physical stickers.
              No login; reads the anonymous publicVerification mirror. */}
          <Route path="/sku/:ttrCode" element={<VerifyPage />} />
          {/* Public sale page — Shop Cleanout 050326 parallel test against HiBid.
              No login required; first bid provisions a guest user from email + name. */}
          <Route path="/sale/:auctionId" element={<SalePage />} />
          <Route path="/sale/:auctionId/info" element={<SaleInfo />} />
          {/* Live admin cockpit for the same sale — sellers/auctioneers only. */}
          <Route path="/admin/sale/:auctionId" element={<SellerRoute><SaleConsole /></SellerRoute>} />
          {/* Admin sale views — AdminRoute (strict: NO consignor), they expose bidder PII. */}
          <Route path="/admin/sale/:auctionId/dashboard" element={<AdminRoute><SaleDashboard /></AdminRoute>} />
          <Route path="/admin/sale/:auctionId/registrations" element={<AdminRoute><SaleRegistrations /></AdminRoute>} />
          <Route path="/admin/sale/:auctionId/lot-stats" element={<AdminRoute><LotStats /></AdminRoute>} />
          <Route path="/admin/sale/:auctionId/settlements" element={<AdminRoute><AdminSaleSettlements /></AdminRoute>} />

          {/* Admin consignor directory + read-only preview of any consignor's portal. */}
          {/* Bidder overview (real) + management stubs — admin/auctioneer only. */}
          <Route path="/admin/bidders" element={<AdminRoute><AdminBidders /></AdminRoute>} />
          <Route path="/admin/bids" element={<AdminRoute><AdminBids /></AdminRoute>} />
          <Route path="/admin/consignors" element={<AdminRoute><AdminConsignors /></AdminRoute>} />
          <Route path="/admin/consignors/:consignorId/dashboard" element={<AdminRoute><ConsignorPreviewDashboard /></AdminRoute>} />
          <Route path="/admin/consignors/:consignorId/contracts/:contractId" element={<AdminRoute><ConsignorPreviewContract /></AdminRoute>} />
          <Route path="/admin/consignors/:consignorId/orders/:coId" element={<AdminRoute><ConsignorPreviewCO /></AdminRoute>} />
          <Route path="/admin/consignors/:consignorId/settlements/:settlementId" element={<AdminRoute><ConsignorPreviewSettlement /></AdminRoute>} />
          {/* Public front door. */}
          {/* `/` decides between the featured two-door landing and the normal
              auction list — see pages/Home.tsx. `/browse` is always the list. */}
          <Route path="/" element={<Home />} />
          <Route path="/browse" element={<PublicAuctionList />} />
          <Route path="/past" element={<PastAuctions />} />
          <Route path="/past/:saleId" element={<PastSaleResults />} />
          <Route path="/past/:saleId/lots/:lotId" element={<PastLotDetail />} />
          {/* Admin Dashboard — sellers/auctioneers only. */}
          <Route path="/dashboard" element={<SellerRoute><Dashboard /></SellerRoute>} />
          <Route path="/import" element={<SellerRoute><ImportCSV /></SellerRoute>} />
          {/* Universal post-auction order importer disabled until utils are wired up — see App.tsx import comment. */}
          {/* <Route path="/admin/import-orders" element={<SellerRoute><ImportOrders /></SellerRoute>} /> */}
          {/* Unified sales picker — native + imported sales side-by-side. */}
          <Route path="/admin/sales" element={<SellerRoute><SalePicker /></SellerRoute>} />
          <Route path="/auctions" element={<SellerRoute><AuctionList /></SellerRoute>} />
          <Route path="/auctions/:id" element={<SellerRoute><AuctionDetails /></SellerRoute>} />
          <Route path="/auctions/:auctionId/update-images" element={<SellerRoute><UpdateImages /></SellerRoute>} />
          <Route path="/auctions/:id/update-csv" element={<UpdateAuctionCSV />} />
          <Route path="/auctions/:auctionId/lots/:lotId" element={<LotDetail />} />
          <Route path="/register-bidder" element={<BidderRegistration />} />
          {/* Bidder routes — any authenticated role. */}
          <Route path="/profile" element={<PrivateRoute><Profile /></PrivateRoute>} />
          <Route path="/my-bids" element={<PrivateRoute><MyBids /></PrivateRoute>} />
          {/* Watching — the bidder's shortlist of lots to bid on (not yet bid). */}
          <Route path="/watching" element={<PrivateRoute><Watching /></PrivateRoute>} />
          {/* Bidder-facing, read-only invoice views (Feature 1). */}
          <Route path="/my-invoices" element={<PrivateRoute><MyInvoices /></PrivateRoute>} />
          <Route path="/my-invoices/:invoiceNumber" element={<PrivateRoute><MyInvoiceDetail /></PrivateRoute>} />

          {/* Consignor self-service — scoped to the signed-in consignor via useConsignorId + rules. */}
          <Route path="/consignor/dashboard" element={<ConsignorRoute><ConsignorDashboard /></ConsignorRoute>} />
          <Route path="/consignor/contracts/:contractId" element={<ConsignorRoute><ConsignorContractView /></ConsignorRoute>} />
          <Route path="/consignor/orders/:coId" element={<ConsignorRoute><ConsignorCOView /></ConsignorRoute>} />
          <Route path="/consignor/settlements/:settlementId" element={<ConsignorRoute><ConsignorSettlementView /></ConsignorRoute>} />
          <Route path="/auctions/:auctionId/invoices" element={<SellerRoute><InvoiceList /></SellerRoute>} />
          <Route path="/invoices/:invoiceNumber" element={<SellerRoute><InvoiceDetail /></SellerRoute>} />
          <Route path="/picker" element={<SellerRoute><PickerInterface /></SellerRoute>} />
          <Route path="/shipping" element={<SellerRoute><ShippingStation /></SellerRoute>} />
          <Route path="/picker/shipping" element={<SellerRoute><ShippingStation /></SellerRoute>} />
          <Route path="/picker/order/:invoiceNumber" element={<SellerRoute><PickOrderView /></SellerRoute>} />
          <Route path="/pickup/:invoiceNumber" element={<SellerRoute><PickupConfirmation /></SellerRoute>} />
          <Route path="/pickup-orders" element={<SellerRoute><PickupOrders /></SellerRoute>} />
        </Routes>
      </main>

      <Toaster
        position="top-right"
        toastOptions={{
          style: { background: '#363636', color: '#fff' },
        }}
      />
    </div>
  );
}

function App() {
  return (
    <HelmetProvider>
      <AuthProvider>
        <Router>
          <AppRoutes />
        </Router>
      </AuthProvider>
    </HelmetProvider>
  );
}

export default App;
